import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useGetPickemsQuery, type Pickem } from '@/api/pickems/getPickems';
import { useDeletePickemMutation } from '@/api/pickems/deletePickem';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useDuplicatePickemMutation } from '@/api/pickems/duplicatePickem';
import { getSportLogoUrl } from '@low6-pickem/sports-config';
import {
  Plus,
  Target,
  Settings,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  BarChart3,
} from 'lucide-react';
import { OpenPublicUrlMenuItem } from '@/components/dashboard/OpenPublicUrlMenuItem';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link, useSearch, useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentAdminUser } from '@/hooks/useCurrentAdminUser';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

export const Route = createFileRoute('/dashboard/pickems/')({
  beforeLoad: ({ context }) => {
    const { allowedGameTypes } = context.adminUser;
    if (allowedGameTypes !== null && !allowedGameTypes.includes('pickem')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: PickemsPage,
});

type PickemData = Pickem & { id: string; label: string };

function PickemsPage() {
  const { canManageContests } = useCurrentAdminUser();
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/dashboard/pickems/' }) as { tenant_id?: string };
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [leagueFilter] = useState<string | undefined>(searchParams.tenant_id);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pickemToDelete, setPickemToDelete] = useState<PickemData | null>(null);

  const { data: tenants = [] } = useGetTenantsQuery();
  const { data: pickems = [], isLoading } = useGetPickemsQuery({
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });
  const deletePickemMutation = useDeletePickemMutation();
  const duplicatePickemMutation = useDuplicatePickemMutation();

  const formattedPickems = pickems
    .filter(pickem => {
      if (leagueFilter && pickem.tenant_id !== leagueFilter) return false;
      if (tenantFilter !== 'all' && pickem.tenant_id !== tenantFilter) return false;
      if (startDate) {
        const pickemStart = pickem.start;
        if (pickemStart && new Date(pickemStart) < new Date(startDate)) return false;
      }
      if (endDate) {
        const pickemStart = pickem.start;
        if (pickemStart && new Date(pickemStart) > new Date(endDate)) return false;
      }
      return true;
    })
    .map((pickem, index) => ({
      ...pickem,
      id: pickem.pickem_id ? pickem.pickem_id.toString() : `temp-${index}`,
      label: pickem.title_en || pickem.title_fr || 'Untitled',
    }));

  const handleEditPickem = (pickem: PickemData) => {
    navigate({
      to: '/dashboard/pickems/$id',
      params: { id: pickem.id },
      search: { tenant_id: undefined, tab: undefined },
    });
  };

  const filteredPickems = formattedPickems;

  const handleDeleteClick = (pickem: PickemData) => {
    setPickemToDelete(pickem);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (pickemToDelete) {
      await deletePickemMutation.mutateAsync(pickemToDelete.id);
      setDeleteDialogOpen(false);
      setPickemToDelete(null);
    }
  };

  const handleDuplicate = async (pickem: PickemData) => {
    if (pickem.tenant_id) {
      await duplicatePickemMutation.mutateAsync({
        pickemId: pickem.pickem_id,
        tenant_id: pickem.tenant_id,
      });
    }
  };

  const handleScoringMode = (pickem: PickemData) => {
    navigate({
      to: '/dashboard/pickems/$id',
      params: { id: pickem.pickem_id },
      search: { tenant_id: undefined, tab: 'live-scoring' },
    });
  };

  const handleLeaderboard = (pickem: PickemData) => {
    navigate({
      to: '/dashboard/pickems/$id',
      params: { id: pickem.pickem_id },
      search: { tenant_id: undefined, tab: 'leaderboard' },
    });
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    const statusMap: Record<string, { label: string; className: string }> = {
      INPLAY: {
        label: 'InPlay',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      },
      ACTIVE: {
        label: 'InPlay',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      },
      OPEN: {
        label: 'Open',
        className: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
      },
      SCHEDULED: {
        label: 'Scheduled',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      },
      DRAFT: {
        label: 'Draft',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      },
      COMPLETE: {
        label: 'Complete',
        className: 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-gray-100',
      },
    };

    const statusInfo = statusMap[status] || {
      label: status,
      className: 'bg-gray-100 text-gray-800',
    };

    return (
      <Badge className={statusInfo.className} variant="outline">
        {statusInfo.label}
      </Badge>
    );
  };

  const getTenantName = (tenantId?: string) => {
    if (!tenantId) return '-';
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    return tenant?.name_en || tenant?.name_fr || tenant?.title || '-';
  };

  return (
    <TooltipProvider>
      <PageContainer>
        <Breadcrumbs overrides={{ pickems: "Pick'em" }} />

        <div className="flex gap-4 items-center mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-500" />
            <h1 className="text-2xl font-semibold">Pick'em</h1>
          </div>

          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Tenants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tenants</SelectItem>
              {tenants.map(tenant => (
                <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.name_en || tenant.name_fr || tenant.title || 'Unnamed Tenant'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="SCHEDULED">Scheduled</SelectItem>
              <SelectItem value="OPEN">Open</SelectItem>
              <SelectItem value="INPLAY">InPlay</SelectItem>
              <SelectItem value="COMPLETE">Complete</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative">
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-[155px] pr-8"
            />
          </div>

          <div className="relative">
            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-[155px] pr-8"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/dashboard/pickems/settings" search={{ tab: 'game-rules' }}>
                  <Button variant="outline" size="icon">
                    <Settings className="h-4 w-4" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Settings</TooltipContent>
            </Tooltip>
            {canManageContests && (
              <Link
                to="/dashboard/pickems/$id"
                params={{ id: 'new' }}
                search={{ tenant_id: undefined, tab: undefined }}
              >
                <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Contest
                </Button>
              </Link>
            )}
          </div>
        </div>

        <ContentCard>
          <Table>
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow>
                <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold w-16">
                  Sport
                </TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">ID</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                  Contest Name
                </TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Tenant</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Start Date</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Users</TableHead>
                <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Status</TableHead>
                <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-6 w-6" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-8 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredPickems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Target className="h-12 w-12 text-gray-400" />
                      <p className="text-gray-600 dark:text-gray-400">No pickems found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPickems.map((pickem: PickemData) => {
                  return (
                    <TableRow key={pickem.pickem_id}>
                      <TableCell>
                        <img
                          src={getSportLogoUrl(pickem.sport)}
                          alt={pickem.sport || 'NFL'}
                          className="h-6 w-6 object-contain"
                        />
                      </TableCell>
                      <TableCell className="font-medium font-mono">
                        {pickem.friendly_id ?? '-'}
                      </TableCell>
                      <TableCell className="font-medium">
                        {pickem.title_en || pickem.title_fr || 'Untitled'}
                      </TableCell>
                      <TableCell>{getTenantName(pickem.tenant_id)}</TableCell>
                      <TableCell>
                        {pickem.start ? (
                          <TzDateDisplay
                            utcDate={pickem.start}
                            showTimezoneTooltip={false}
                            showUtcBadge={false}
                            formatString="MMM d, yyyy h:mm a"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {pickem.entry_count !== undefined
                          ? pickem.entry_count.toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(pickem.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditPickem(pickem)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(pickem)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleScoringMode(pickem)}>
                              <Target className="mr-2 h-4 w-4" />
                              Scoring Mode
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleLeaderboard(pickem)}>
                              <BarChart3 className="mr-2 h-4 w-4" />
                              Leaderboard
                            </DropdownMenuItem>
                            <OpenPublicUrlMenuItem
                              gameType="pickem"
                              tenantId={pickem.tenant_id}
                              gameId={pickem.pickem_id}
                            />
                            {canManageContests && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDeleteClick(pickem)}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ContentCard>
      </PageContainer>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pickem</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting pickems with entries may upset your users! Are you sure you want to delete "
              {pickemToDelete?.title_en || pickemToDelete?.title_fr}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
